import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const num = (v) => (v === "" || v == null ? null : Math.max(0, parseInt(v, 10) || 0));

const PartyRows = ({ rows, onChange, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <Input value={r.party} placeholder="Party" onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, party: e.target.value } : x)))} data-testid={`${testid}-party-${i}`} />
        <Input className="w-24" type="number" value={r.seats ?? ""} placeholder="Seats" onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, seats: num(e.target.value) } : x)))} data-testid={`${testid}-seats-${i}`} />
        <Button size="icon" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove party"><Trash2 size={14} /></Button>
      </div>
    ))}
    <Button size="sm" variant="ghost" onClick={() => onChange([...rows, { party: "", seats: null }])} data-testid={`${testid}-add`}><Plus size={14} className="mr-1" />Add party</Button>
  </div>
);

const clean = (rows) => rows.filter((r) => r.party.trim() && r.seats != null).map((r) => ({ party: r.party.trim(), seats: r.seats }));

export const ElectionEditor = ({ open, onOpenChange, liveId, brief, onSaved }) => {
  const [overview, setOverview] = useState("");
  const [chambers, setChambers] = useState([]);
  useEffect(() => {
    if (!open) return;
    setOverview(brief.overview || "");
    setChambers((brief.chambers || []).map((c) => ({ ...c, held_by: c.held_by || [], won_by: c.won_by || [] })));
  }, [open, brief]);
  const set = (i, patch) => setChambers(chambers.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const save = async () => {
    try {
      const seats = (list) => JSON.stringify(list.map((c) => [c.name, c.total_seats, c.contested, clean(c.held_by)]));
      const patch = {};
      if (overview.trim() !== (brief.overview || "").trim()) patch.overview = overview;
      if (seats(chambers) !== seats((brief.chambers || []).map((c) => ({ ...c, held_by: c.held_by || [] })))) patch.chambers = chambers.map((c) => ({ name: c.name, total_seats: c.total_seats, contested: c.contested, held_by: clean(c.held_by) }));
      let data = brief;
      if (Object.keys(patch).length) ({ data } = await api.patch(`/admin/live/${liveId}/election`, patch));
      for (const c of chambers) {
        const won = clean(c.won_by);
        if (JSON.stringify(won) !== JSON.stringify(clean((brief.chambers || []).find((x) => x.name === c.name)?.won_by || [])))
          ({ data } = await api.post(`/admin/live/${liveId}/election/won`, { chamber: c.name, action: "set", parties: won }));
      }
      toast.success("Election brief saved"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-xl overflow-y-auto" data-testid="election-editor">
        <DialogHeader><DialogTitle>Edit election brief</DialogTitle>
          <DialogDescription>Your edits stay in place: the daily AI update won't overwrite them. Clear the summary to let the AI write it again.</DialogDescription></DialogHeader>
        <label className="text-sm font-medium">What's happening and what the result could decide</label>
        <Textarea rows={4} value={overview} onChange={(e) => setOverview(e.target.value)} data-testid="election-editor-overview" />
        {chambers.map((c, i) => (
          <div key={c.name} className="space-y-3 rounded-xl border border-border p-4" data-testid={`election-editor-chamber-${i}`}>
            <p className="font-serif text-lg">{c.name}</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-500">Seats contested<Input type="number" value={c.contested ?? ""} onChange={(e) => set(i, { contested: num(e.target.value) })} data-testid={`election-editor-contested-${i}`} /></label>
              <label className="text-xs text-slate-500">Total seats<Input type="number" value={c.total_seats ?? ""} onChange={(e) => set(i, { total_seats: num(e.target.value) })} data-testid={`election-editor-total-${i}`} /></label>
            </div>
            <p className="text-xs font-semibold text-slate-500">Contested seats currently held by</p>
            <PartyRows rows={c.held_by} onChange={(v) => set(i, { held_by: v })} testid={`election-editor-held-${i}`} />
            <p className="text-xs font-semibold text-slate-500">Who won (shows to members as confirmed)</p>
            <PartyRows rows={c.won_by} onChange={(v) => set(i, { won_by: v })} testid={`election-editor-won-${i}`} />
          </div>
        ))}
        <Button className="rounded-full" onClick={save} data-testid="election-editor-save">Save</Button>
      </DialogContent>
    </Dialog>
  );
};
